import { AppBskyFeedPost } from '@atproto/api'
import { getCachedPost, upsertPost } from '../cache/db'

const APPVIEW = 'https://public.api.bsky.app'
const BATCH_SIZE = 25
const FLUSH_MS = 25
const MAX_PARALLEL = 6
const RETRIES = 3

type PostRecord = AppBskyFeedPost.Record

type Resolver = (post: PostRecord | null) => void

const pending = new Map<string, Resolver[]>()
let flushTimer: ReturnType<typeof setTimeout> | null = null
let inFlight = 0

function scheduleFlush(): void {
  if (flushTimer != null) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void drain()
  }, FLUSH_MS)
}

async function drain(): Promise<void> {
  while (inFlight < MAX_PARALLEL && pending.size > 0) {
    const uris: string[] = []
    for (const uri of pending.keys()) {
      uris.push(uri)
      if (uris.length >= BATCH_SIZE) break
    }
    const claimed = new Map<string, Resolver[]>()
    for (const uri of uris) {
      const arr = pending.get(uri)!
      claimed.set(uri, arr)
      pending.delete(uri)
    }
    inFlight += 1
    void fetchBatch(claimed).finally(() => {
      inFlight -= 1
      if (pending.size > 0) void drain()
    })
  }
}

async function fetchBatch(batch: Map<string, Resolver[]>): Promise<void> {
  const uris = Array.from(batch.keys())
  const u = new URL(`${APPVIEW}/xrpc/app.bsky.feed.getPosts`)
  for (const uri of uris) u.searchParams.append('uris', uri)

  let attempt = 0
  let posts: Array<{ uri: string; record?: unknown; cid?: string }> = []
  while (attempt < RETRIES) {
    try {
      const r = await fetch(u.toString(), { headers: { Accept: 'application/json' } })
      if (r.status === 429 || r.status === 503) {
        const retry = Number(r.headers.get('retry-after') ?? '2')
        await delay(Math.min(Math.max(retry, 1), 15) * 1000)
        attempt++
        continue
      }
      if (!r.ok) break
      const data = (await r.json()) as { posts?: Array<{ uri: string; record?: unknown; cid?: string }> }
      posts = data.posts ?? []
      break
    } catch {
      attempt++
      await delay(400 * 2 ** attempt)
    }
  }

  const byUri = new Map<string, PostRecord | null>()
  for (const p of posts) {
    if (AppBskyFeedPost.isRecord(p.record)) {
      byUri.set(p.uri, p.record)
      // Persist to cache so future visits skip the network entirely.
      const at = parseAt(p.uri)
      if (at) {
        void upsertPost({
          uri: p.uri,
          cid: p.cid ?? null,
          did: at.did,
          rkey: at.rkey,
          value: p.record as unknown as Record<string, unknown>,
          fetched_at: Date.now(),
        }).catch(() => undefined)
      }
    } else {
      byUri.set(p.uri, null)
    }
  }
  for (const [uri, resolvers] of batch) {
    const post = byUri.has(uri) ? byUri.get(uri)! : null
    for (const fn of resolvers) fn(post)
  }
}

function parseAt(uri: string): { did: string; rkey: string } | null {
  // at://did:plc:xxx/app.bsky.feed.post/rkey
  const m = uri.match(/^at:\/\/([^/]+)\/[^/]+\/([^/]+)$/)
  if (!m) return null
  return { did: m[1], rkey: m[2] }
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms))
}

/**
 * Resolve a post record for a like/repost subject. First checks the IndexedDB
 * cache; on miss, joins a batched AppView lookup. Always resolves — null means
 * "post is gone or unavailable", so callers can stop showing a spinner.
 */
export async function loadHydratedPost(uri: string): Promise<PostRecord | null> {
  const cached = await getCachedPost(uri).catch(() => undefined)
  if (cached?.value && AppBskyFeedPost.isRecord(cached.value)) {
    return cached.value
  }
  return new Promise<PostRecord | null>((resolve) => {
    const arr = pending.get(uri)
    if (arr) arr.push(resolve)
    else pending.set(uri, [resolve])
    scheduleFlush()
  })
}
